import { z } from 'zod';
import { comparatorSchema } from './comparator';
import { singleGeoLocationSchema, multiGeoLocationSchema } from './geoLocation';
import { dateRangeSchema } from './dateRange';
import {
  validateNumberValue,
  validateDateValue,
  validateEmptyValue,
  validateEmptyComparator,
  validateRegexValue,
} from '../utils';
import { FieldType, CastType, ClientDataType } from '../../types';
import { useValidateForHandlebarValue } from '../hooks/useValidateForHandlebarValue';
import { isString } from 'utils/tsHelpers';
import { applyConditionTransformations } from '../utils/transformations';

export const conditionDataSchema = z.object({
  dataType: z.nativeEnum(ClientDataType),
  field: z.string(),
  comparatorType: comparatorSchema.optional(),
  castType: z.nativeEnum(CastType).optional(),
});

export const stringValueCondition = z.object({
  ...conditionDataSchema.shape,
  fieldType: z.enum([FieldType.KEYWORD, FieldType.STRING]),
  value: z.string().optional(),
  values: z.array(z.string()).nonempty().optional(),
  castType: z.nativeEnum(CastType).optional(),
});

export const numberValueCondition = z.object({
  ...conditionDataSchema.shape,
  castType: z.nativeEnum(CastType).optional(),
  fieldType: z.enum([FieldType.INTEGER, FieldType.FLOAT]),
  value: z.string().or(z.number()).pipe(z.coerce.string()).optional(),
  values: z.array(z.string()).nonempty().optional(),
});

export const booleanValueCondition = z.object({
  ...conditionDataSchema.shape,
  fieldType: z.enum([FieldType.BOOLEAN]),
  value: z.string().optional(),
  values: z.undefined(),
  castType: z.nativeEnum(CastType).optional(),
});

export const dateValueCondition = z.object({
  ...conditionDataSchema.shape,
  fieldType: z.enum([FieldType.DATE]),
  value: z.string().or(dateRangeSchema).optional(),
  values: z.undefined(),
  castType: z.nativeEnum(CastType).optional(),
});

export const geoLocationValueCondition = z.object({
  ...conditionDataSchema.shape,
  fieldType: z.enum([FieldType.GEO_LOCATION]),
  value: z.string().or(singleGeoLocationSchema).default(''),
  values: z.undefined(),
  castType: z.nativeEnum(CastType).optional(),
  ...singleGeoLocationSchema.shape,
  ...multiGeoLocationSchema.shape,
});

export const nestedValueCondition = z.object({
  ...conditionDataSchema.shape,
  fieldType: z.enum([FieldType.NESTED, FieldType.OBJECT]),
  value: z.string().default(''),
  values: z.undefined(),
  castType: z.nativeEnum(CastType).optional(),
});

export const campaignLabelIdCondition = z.object({
  ...conditionDataSchema.shape,
  fieldType: z.enum([FieldType.CAMPAIGN_LABEL_ID]),
  value: z.string().or(z.number()).pipe(z.coerce.string()).optional(),
  values: z.array(z.string()).nonempty().optional(),
});

/** condition for when fieldType is undefined */
export const undefinedValueCondition = z.object({
  ...conditionDataSchema.shape,
  fieldType: z.undefined().transform(() => FieldType.UNDEFINED),
  value: z.string().optional(),
  values: z.array(z.string()).nonempty().optional(),
  castType: z.nativeEnum(CastType).optional(),
});

export const useConditionSchema = () => {
  const validateForHandlebarValue = useValidateForHandlebarValue();
  return z
    .discriminatedUnion('fieldType', [
      stringValueCondition,
      numberValueCondition,
      booleanValueCondition,
      dateValueCondition,
      geoLocationValueCondition,
      nestedValueCondition,
      campaignLabelIdCondition,
      undefinedValueCondition,
    ])
    .superRefine(({ value, values, comparatorType, fieldType, castType }, { addIssue }) => {
      validateEmptyValue({ value, values }, addIssue);
      if (fieldType === FieldType.DATE) {
        validateDateValue({ value, fieldType, comparatorType }, addIssue);
        // Validate for handlebar if date is relative date string
        if (isString(value)) {
          validateForHandlebarValue({ value }, addIssue);
        }
      } else if (fieldType !== FieldType.GEO_LOCATION) {
        validateForHandlebarValue({ value }, addIssue);
        validateRegexValue({ value, comparatorType }, addIssue);
        validateNumberValue({ value, values, fieldType, castType, comparatorType }, addIssue);
      }
      validateEmptyComparator({ comparatorType }, addIssue);
    })
    .transform(applyConditionTransformations);
};
